import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../../contexts/AdminContext";

const trustItems = [
  { icon: "ri-shield-check-line", label: "Notariell beglaubigter Preis", color: "text-secondary" },
  { icon: "ri-award-line", label: "Notariell beglaubigte Ziehung", color: "text-sky-500" },
  { icon: "ri-lock-line", label: "Sichere SSL-Zahlung", color: "text-primary" },
  { icon: "ri-time-line", label: "Persönliche Übergabe", color: "text-primary" },
];

export default function PropertySidebar() {
  const { settings } = useAdmin();
  
  const displayPackages = useMemo(() => {
    if (settings.packages && settings.packages.length > 0) return settings.packages;
    return [
      { id: "starter", name: "Starter", count: 5, price: "25€" },
      { id: "classic", name: "Klassik", count: 17, price: "65€", popular: true },
      { id: "premium", name: "Premium", count: 43, price: "130€" },
    ];
  }, [settings.packages]);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const pkg = displayPackages[selectedIdx] || displayPackages[0];

  const daysLeft = useMemo(() => {
    const end = new Date(settings.nextDrawDate).getTime();
    const now = new Date().getTime();
    const diff = end - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [settings.nextDrawDate]);

  const formattedDate = new Date(settings.nextDrawDate).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });

  return (
    <div className="sticky top-28 flex flex-col gap-5">

      {/* Price card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 px-5 py-4">
          <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Gewinnwert</p>
          <p className="font-playfair text-3xl font-bold text-white">{settings.lotteryValue}</p>
          <p className="text-slate-400 text-[10px] mt-1 line-clamp-1">{settings.propertyTitle} · {settings.propertyLocation}</p>
        </div>

        <div className="px-5 py-4">
          {/* Date */}
          <div className="flex items-center gap-2 bg-orange-50 rounded-xl px-3 py-2.5 mb-4 border border-orange-100">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-event-line text-primary"></i>
            </span>
            <div>
              <p className="text-orange-700 font-bold text-xs">Ziehung am {formattedDate}</p>
              <p className="text-primary text-[10px]">Noch {daysLeft} Tage zum Teilnehmen</p>
            </div>
          </div>

          {/* Package selector */}
          <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-3">Wählen Sie Ihr Paket</p>
          <div className="space-y-2 mb-4">
            {displayPackages.map((p, i) => (
              <button
                key={i}
                onClick={() => setSelectedIdx(i)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all duration-200 text-left ${
                  selectedIdx === i ? "border-slate-900 bg-slate-50" : "border-slate-100 bg-white hover:border-slate-200"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${selectedIdx === i ? "bg-slate-900" : "bg-slate-200"}`} />
                  <div>
                    <span className="text-slate-800 text-sm font-semibold">{p.name}</span>
                    {p.popular && (
                      <span className="ml-2 text-[10px] bg-orange-100 text-primary-dark font-bold px-1.5 py-0.5 rounded-full">BELIEBT</span>
                    )}
                    <p className="text-slate-400 text-[10px]">{p.count} Lose</p>
                  </div>
                </div>
                <span className="text-slate-900 font-bold text-sm">{p.price}</span>
              </button>
            ))}
          </div>

          {/* CTA */}
          <Link
            to={`/paiement?package=${selectedIdx}`}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:-translate-y-0.5"
          >
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-ticket-2-line"></i></span>
            Teilnehmen — {pkg.price}
          </Link>

          <Link
            to="/forfaits"
            className="w-full flex items-center justify-center gap-1.5 text-slate-500 hover:text-slate-900 text-xs font-medium mt-3 cursor-pointer transition-colors"
          >
            Alle Pakete anzeigen
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </div>

      {/* Trust badges */}
      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
        <div className="grid grid-cols-2 gap-3">
          {trustItems.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${t.icon} ${t.color} text-sm`}></i>
              </span>
              <span className="text-slate-600 text-xs leading-tight">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Share */}
      <div className="flex items-center justify-center gap-3">
        <span className="text-slate-400 text-xs">Teilen:</span>
        {[
          { icon: "ri-facebook-line", color: "hover:text-sky-600" },
          { icon: "ri-twitter-x-line", color: "hover:text-slate-900" },
          { icon: "ri-whatsapp-line", color: "hover:text-secondary" },
          { icon: "ri-links-line", color: "hover:text-primary" },
        ].map((s, i) => (
          <button key={i} className={`w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 ${s.color} transition-colors cursor-pointer`}>
            <i className={s.icon}></i>
          </button>
        ))}
      </div>
    </div>
  );
}
